import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Company } from 'src/app/domains/companies/models';
import { EquipmentContent, Handset } from 'src/app/domains/equipment/models';
import { EquipmentService } from 'src/app/domains/equipment/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { AddHandsetPopupComponent } from '../add-handset-popup/add-handset-popup.component';
import { AssignToUserPopupComponent } from '../assign-to-user-popup/assign-to-user-popup.component';
import { ChangeOutboundDidPopupComponent } from '../change-outbound-did-popup/change-outbound-did-popup.component';

@Component({
  selector: 'gdp-single-equipment-air-section',
  templateUrl: './single-equipment-air-section.component.html',
  styleUrls: ['./single-equipment-air-section.component.scss']
})
export class SingleEquipmentAirSectionComponent implements OnInit {
  public loading: boolean = false;
  public handset: Handset;
  @Input() equipment: EquipmentContent;
  @Input() company: Company;
  @Input() set handsetChanged(change: boolean) {
    change ? this.getEquipmentByMacAddress() : '';
  }
  @Output() onChangeHandset: EventEmitter<any> = new EventEmitter<any>();
  private loadingSubscription: Subscription;

  constructor(
    private modalService: NgbModal,
    private notification: NotificationService,
    private equipmentService: EquipmentService,
    private translate: TranslateService
  ) { }

  ngOnInit() { }

  getEquipmentByMacAddress() {
    this.loading = true;
    this.equipmentService.getEquipmentByMacAddress(this.equipment.mac_address,
      [
        {
          status: 404,
          message: 'equipment.filters.errors.macAddress-not-found'
        },
        {
          status: 500,
          message: 'equipment.filters.errors.macAddress-invalid'
        }
      ])
    .subscribe((res: any) => {
      this.loading = false;
      this.equipment = res;
    }, () => {
        this.loading = false;
      });
  }

  openAddHandsetPopup() {
    const modal = this.modalService.open(AddHandsetPopupComponent, {
      size: 'md'
    });
    modal.componentInstance.equipment = this.equipment;
    modal.componentInstance.onAddHandset.pipe(take(1)).subscribe((res: EquipmentContent) => {
      this.getEquipmentByMacAddress();
    }, () => {
        this.loading = false;
      });
  }

  deleteHandset(handset: Handset) {
    this.notification.showConfirmation('equipment.details.handsets.confirm-delete').then((result: string) => {
      if (result === 'ok') {
        handset.isLoading = true;
        this.equipmentService.deleteHandset(this.equipment.mac_address, handset.ipui).subscribe((res: any) => {
          this.equipment.handsets = this.equipment.handsets.filter((el) => el.id !== handset.id);
          this.notification.success(
            this.translate.instant(
              'equipment.details.handsets.delete-success',
              { value: handset.ipui }
            )
          );
          handset.isLoading = false;
        }, () => {
            handset.isLoading = false;
          });
      }
    });
  }

  openAssignToUserPopup(handset: Handset) {
    const modal = this.modalService.open(AssignToUserPopupComponent, {
      size: 'md'
    });
    modal.componentInstance.equipment = this.equipment;
    modal.componentInstance.handset = handset;
    this.handset = handset
    this.loadingSubscription = modal.componentInstance.onLoadingAssign.pipe().subscribe((res: any) => {
      handset.isLoading = res;
    });
    modal.componentInstance.onAssignHandsetToUser.pipe(take(1)).subscribe((res: EquipmentContent) => {
      this.handset.user = res.user;
      this.handset.outbound_did = res.outbound_did;
      handset.isLoading = false;
    });
  }

  unassignFromUser(handset: Handset) {
    this.handset = handset
    this.notification.showConfirmation('equipment.details.handsets.confirm-unassign').then((result: string) => {
      if (result === 'ok') {
        handset.isLoading = true;
        this.equipmentService.unassignHandsetFromUser(
          this.equipment.pbx.id,
          this.handset.user.id,
          this.equipment.mac_address,
          this.handset.ipui
        ).subscribe((res: any) => {
          this.notification.success(
            this.translate.instant(
              'equipment.details.handsets.unassign-success',
              {
                handset: this.handset.ipui,
                firstName: this.handset.user.first_name,
                lastName: this.handset.user.last_name,
                extension: this.handset.user.extension.number
              })
          );
          handset.isLoading = false;
          this.handset.user = res.user;
          this.handset.outbound_did = res.outbound_did;
        }, () => {
            handset.isLoading = false;
          });
      }
    });
  }

  updateOutboundDid(handset: Handset) {
    const modalRef = this.modalService.open(ChangeOutboundDidPopupComponent);
    modalRef.componentInstance.currentDid = handset.outbound_did;
    modalRef.componentInstance.pbxId = this.equipment.pbx.id;
    modalRef.result.then((result: string) => {
      if (!result) return;
      handset.isLoading = true;
      this.equipmentService.updateHandsetOutboundDid(this.equipment.mac_address, handset.ipui, result).subscribe(
        (response: Handset) => {
          const oldDid = handset.outbound_did || '-';
          handset.outbound_did = response.outbound_did;
          this.notification.success(
            this.translate.instant(
              'equipment.details.handsets.outbound-did-success',
              {
                ipui: handset.ipui,
                oldDid: oldDid,
                newDid: response.outbound_did
              }
            )
          );
          handset.isLoading = false;
        }, () => {
          handset.isLoading = false;
        }
      );
    })
  }

  ngOnDestroy() {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

}
